import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

// Components

import InstructionalText from '../notifications/InstructionalText';
import Form from '@lib/wrappers/Form';
import UniversalInput from '@lib/wrappers/UniversalInput';
import ValidationErrorMessage from '../notifications/ValidationErrorMessage';

// ActionCreators

import { changeData } from '@actions/ssap/ssapActionCreators';

// Utils

import { cloneObject } from '@utils/miscellaneous/cloneObject';
import { normalizeNameOfPerson } from '@utils/normalize/normalizeNameOfPerson';
import { preventerTools } from '@utils/tools/preventerTools';
import { omit, set } from '@utils/miscellaneous/objectHelpers';

// Constants

import { EMPTY_OBJECTS } from '@constants/ssap/initialValues/emptyObjects';
import { DROPDOWN_OPTIONS } from '@constants/ssap/hhmTypes/dropdownOptions';
import { INPUT_TYPES } from '@constants/inputs/inputTypes';
import { OTHER_PROGRAMS_LIST, OTHER_PROGRAMS_TYPES } from '@constants/ssap/hhmTypes/otherProgramsList';
import { VALIDATION_TYPES } from '@constants/inputs/validationTypes';
import { VALIDATION_ERROR_MESSAGES } from '@constants/inputs/validationErrorMessages';
import { useExchangeAndProgramNames } from '@reducers/appReducer';
import { selectIfQhpAllowedForMedicare } from '@reducers/appReducer';
import { TIME_INTERVALS } from '@utils/dates/dateMethods';
import { addToDate } from '@utils/dates/date.utils';
import { parseFieldChangeArgs } from '@utils/ssap/parseFieldChangeArgs';
import { validateMedicareStartDate, checkSecondOrThirdFutureMonth } from '@utils/validators/customValidations';
import { MedicaidCoverageDecision } from '../SsapTypes';



/**
 *
 * @param {Object} props
 * @returns {React.Component} - 'Additional Information - Member Enrolled In Other Health Insurance
 *
 */
const Page4_61 = props => {

    const {
        CHIP,
        MARKETPLACE_COVERAGE,
        NONE,
        OTHER_COVERAGE
    } = OTHER_PROGRAMS_TYPES;

    const {
        data,
        environment: {
            isState,
            localDates: { serverDate }
        },
        form,
        formName,
        householdMembers
    } = props;

    const {
        isFormSubmitted
    } = preventerTools.provideObject(form[formName]);

    const {
        hhmIndex : member,
        currentHhm
    } = props;

    const {
        healthCoverage : {
            currentlyHasHealthInsuranceIndicator,
            currentOtherInsurance: {
                otherStateOrFederalPrograms = []
            },
            medicareInsurance: {
                enrolled = null,
                startDate = null,
                marketplaceCoverage = 'UNSPECIFIED'
            } = {}
        },
        name,
        dateOfBirth
    } = currentHhm;

    const medicareDate = useMemo(() => validateMedicareStartDate(startDate, dateOfBirth, serverDate), [startDate]);
    const isFutureMonths2_3 = useMemo(() => checkSecondOrThirdFutureMonth(startDate, serverDate), [startDate]);
    const dualCoverage = useMemo(() => marketplaceCoverage === MedicaidCoverageDecision.DUAL_COVERAGE, [marketplaceCoverage]);

    const { exchangeName, exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip } = useExchangeAndProgramNames();
    const qhpAllowedForMedicare = useSelector(selectIfQhpAllowedForMedicare);

    const currentEmployerInsurance = (
        currentEmployerArr =>
            currentEmployerArr.length > 0 ?
                currentEmployerArr[0].currentEmployerInsurance :
                {})(householdMembers[member].healthCoverage.currentEmployer);

    const otherInsurance = householdMembers[member]
        .healthCoverage
        .otherInsurance;

    const prepareValuesForOtherProgramTypes = () => {

        return OTHER_PROGRAMS_LIST.reduce((programs, program) => {

            const { notSupported, type } = program;

            switch(true) {
                case notSupported: // Skip not supported coverage options
                case (isState.IL || isState.MN || isState.NJ || isState.OR) && type === MARKETPLACE_COVERAGE: // Skip MARKETPLACE coverage option for IL, MN, NJ, OR states
                case isState.NM && type === CHIP: // Skip CHIP coverage option for NM state
                    return programs;
                default:
                {
                    const matchingProgramEligibility = (pr => pr ? pr.eligible : false)(otherStateOrFederalPrograms
                        .find(pr => pr.type === type));

                    return [
                        ...programs,
                        {
                            ...program,
                            eligible: matchingProgramEligibility
                        }
                    ];
                }
            }
        }, []);
    };

    const [ otherPrograms, setOtherPrograms ] = useState(prepareValuesForOtherProgramTypes());

    useEffect(() => {

        const newState = { ...data };

        const memberReference = `taxHousehold.householdMember[${ member }].`;
        const currentMember = newState.taxHousehold.householdMember[member];
        const isMedicareCoveragePresent = currentMember.healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms?.find(pr => pr.type === OTHER_PROGRAMS_TYPES.MEDICARE);

        if (isMedicareCoveragePresent?.eligible) {
            set(
                newState,
                memberReference + 'healthCoverage.medicareInsurance.enrolled',
                true
            );
        }

        const normalizedOtherPrograms = otherPrograms
            .reduce((programs, program) => {
                return program.eligible ?
                    [
                        ...programs,
                        omit(program, 'nameId')
                    ] :
                    programs;
            }, []);

        const shouldCleanOtherCoverageDetails = !~normalizedOtherPrograms.findIndex(pr => pr.type === OTHER_COVERAGE);

        set(
            newState,
            memberReference + 'healthCoverage.currentOtherInsurance.otherStateOrFederalPrograms',
            normalizedOtherPrograms
        );

        if(shouldCleanOtherCoverageDetails) {
            set(
                newState,
                memberReference + 'healthCoverage.otherInsurance',
                { ...EMPTY_OBJECTS.OTHER_INSURANCE }
            );
        }

        props.actions.changeData(newState);

    }, [ JSON.stringify(otherPrograms) ]);

    const onFieldChange = (...args) => {

        let { name, newData, value } = parseFieldChangeArgs(args);

        const newState = { ...data };

        if(name === 'currentlyHasHealthInsuranceIndicator' && value === false) {
            newData = {
                ...newData,
                healthCoverage: {
                    ...newData.healthCoverage,
                    otherInsurance: { ...EMPTY_OBJECTS.OTHER_INSURANCE },
                    currentOtherInsurance: {
                        ...newData.healthCoverage.currentOtherInsurance
                    }
                }
            };
            setOtherPrograms(otherPrograms => otherPrograms.map(pr => ({
                ...pr,
                eligible: false
            })));
        }

        newState.taxHousehold.householdMember[member] = { ...newData };

        props.actions.changeData(newState);
    };

    const onFieldChange_programType = (...args) => {
        const [ newData ] = args;

        if (newData.type !== NONE) {
            setOtherPrograms(otherPrograms =>
                otherPrograms.map(pr => pr.type === newData.type ?
                    {
                        ...newData,
                        eligible: !newData.eligible
                    } : pr.type === NONE ?
                        {
                            ...pr,
                            eligible: false
                        } : pr
                ));
        } else {
            setOtherPrograms(otherPrograms =>
                otherPrograms.map(pr => {
                    return pr.type !== newData.type ?
                        {
                            ...pr,
                            eligible: false
                        } :
                        {
                            ...newData,
                            eligible: !newData.eligible
                        };
                }));
        }
    };

    const onFieldChange_otherInsurance = newData => {
        const newState = { ...data };

        newState
            .taxHousehold
            .householdMember[member]
            .healthCoverage
            .otherInsurance = newData;

        props.actions.changeData(newState);
    };

    const onFieldChange_medicareInsurance = (...args) => {
        let { name, newData, value } = parseFieldChangeArgs(args);

        const newState = { ...data };
        let medicareInsurance = newData.healthCoverage.medicareInsurance;

        if (name === 'enrolled' && value === false) {
            // Clear all Medicare data if user selects "No"
            medicareInsurance = { ...EMPTY_OBJECTS.MEDICARE_INSURANCE };
            medicareInsurance.enrolled = false;
        } else if (name === 'startDate') {
            const newMedicareDate = validateMedicareStartDate(value, dateOfBirth, serverDate);
            const isNewDateFutureMonths2_3 = checkSecondOrThirdFutureMonth(value, serverDate);
            if (medicareDate.isValid !== newMedicareDate.isValid || isFutureMonths2_3 !== isNewDateFutureMonths2_3) {
                if (newMedicareDate.isValid && !isNewDateFutureMonths2_3) {
                    medicareInsurance.marketplaceCoverage = MedicaidCoverageDecision.END_MARKETPLACE; // By default set to END_MARKETPLACE if checkbox option displayed
                } else {
                    medicareInsurance.marketplaceCoverage = 'UNSPECIFIED';
                }
            }
        } else if (name === 'dualCoverage') {
            medicareInsurance.marketplaceCoverage = value ? MedicaidCoverageDecision.DUAL_COVERAGE : MedicaidCoverageDecision.END_MARKETPLACE;
        }

        newData.healthCoverage.medicareInsurance = medicareInsurance;

        newState.taxHousehold.householdMember[member] = { ...newData };
        props.actions.changeData(newState);
    };

    const checkTopLevelValidation = () => {
        const { currentlyHasHealthInsuranceIndicator } = householdMembers[member].healthCoverage;
        return !currentlyHasHealthInsuranceIndicator ? true  : otherPrograms.some(pgm => pgm.eligible);
    };

    return (
        <Fragment>
            <Form
                topLevelValidity = { checkTopLevelValidation() }
                { ...{ formName } }
            >
                <div
                    className = 'fade-in'
                    id = 'aiOtherInsurance'
                >
                    <div className = 'subsection' >
                        <UniversalInput
                            legend={{
                                legendText: 'ssap.input.legend.medicare_enrolled',
                                legendTextReplacements: {
                                    personName: (<strong>{normalizeNameOfPerson(name)}</strong>)
                                },
                                dataInstructions: 'instructional_text.medicare_coverage_enrolled',
                                replacements: { exchangeName }
                            }}
                            label={{
                                showRequiredIcon: true
                            }}
                            inputData={{
                                isInputRequired: true,
                                type: INPUT_TYPES.RADIO_BUTTONS,
                                name: 'enrolled',
                                value: enrolled,
                                options: DROPDOWN_OPTIONS.YES_NO,
                                currentData: householdMembers[member],
                                validationType: VALIDATION_TYPES.CHOSEN,
                                fields: ['healthCoverage', 'medicareInsurance'],
                                isInputStandalone: true

                            }}
                            inputActions={{
                                onFieldChange: onFieldChange_medicareInsurance
                            }}
                            errorClasses={['margin-l-0']}
                        />

                        {enrolled && (
                            <Fragment>
                                <UniversalInput
                                    label={{
                                        text: 'ssap.input.label.medicare_start_date',
                                        labelClass: ['grid-row p-0'],
                                        labelTextReplacements: {
                                            personName: (<strong>{normalizeNameOfPerson(name)}</strong>)
                                        },
                                        dataInstructions: 'instructional_text.medicare_start_date',
                                        replacements: { exchangeName }
                                    }}
                                    containerClass={['mt-0 pb-3']}
                                    inputData={{
                                        isInputRequired: true,
                                        type: INPUT_TYPES.DATE_PICKER,
                                        name: 'startDate',
                                        helperClasses: [],
                                        value: startDate,
                                        valuesToMatch: [dateOfBirth, serverDate],
                                        validationType: VALIDATION_TYPES.MEDICARE_START_DATE,
                                        validationErrorMessage: medicareDate.errorMessage,
                                        currentData: householdMembers[member],
                                        fields: ['healthCoverage', 'medicareInsurance'],
                                        isInputStandalone: true
                                    }}
                                    errorClasses={['margin-l-0']}
                                    inputActions={{
                                        onFieldChange: onFieldChange_medicareInsurance
                                    }}
                                />

                                {medicareDate.isValid && (
                                    <Fragment>
                                        {isFutureMonths2_3
                                            ? <UniversalInput
                                                legend={{
                                                    legendText: 'ssap.input.legend.medicare_marketplace_coverage',
                                                    legendTextReplacements: {
                                                        personName: (<strong>{normalizeNameOfPerson(name)}</strong>),
                                                        exchangeName
                                                    },
                                                    dataInstructions: 'instructional_text.medicare_marketplace_coverage',
                                                    replacements: { exchangeName }
                                                }}
                                                label={{
                                                    showRequiredIcon: true
                                                }}
                                                containerClass={['margin-t-20']}
                                                inputData={{
                                                    isInputRequired: true,
                                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                                    name: 'marketplaceCoverage',
                                                    value: marketplaceCoverage !== 'UNSPECIFIED' ? marketplaceCoverage : '',
                                                    options: DROPDOWN_OPTIONS.MEDICAID_MARKETPLACE_DUAL_COVERAGE(qhpAllowedForMedicare),
                                                    optionsReplacements: { personName: normalizeNameOfPerson(name), exchangeName },
                                                    validationType: VALIDATION_TYPES.CHOSEN,
                                                    currentData: householdMembers[member],
                                                    fields: ['healthCoverage', 'medicareInsurance'],
                                                    isInputStandalone: true
                                                }}
                                                inputActions={{
                                                    onFieldChange: onFieldChange_medicareInsurance
                                                }}
                                                errorClasses={['margin-l-0']}
                                            />

                                            : qhpAllowedForMedicare && <div className='margin-t-20 gi-checkbox'>
                                                <fieldset className=''>
                                                    <legend data-instructions='medicare_dual_coverage' className='gi-checkbox margin-b-10'>
                                                        <span id='medicare_dual_coverage_legend_text'>
                                                            <FormattedMessage
                                                                id='ssap.input.legend.medicare_dual_coverage'
                                                                values={{
                                                                    exchangeName,
                                                                    personName: (
                                                                        <strong>
                                                                            {normalizeNameOfPerson(name)}
                                                                        </strong>
                                                                    )
                                                                }}
                                                            />
                                                        </span>
                                                        <InstructionalText
                                                            Component={
                                                                <span
                                                                    data-instructions='instructional_text.medicare_dual_coverage'
                                                                    className='required-false margin-t-20'
                                                                />
                                                            }
                                                            replacements={{
                                                                personName: name.firstName, exchangeName
                                                            }}
                                                            skipParsing={true}
                                                        />
                                                    </legend>

                                                    <div role='group' aria-labelledby='medicare_dual_coverage_legend_text'>
                                                        <UniversalInput
                                                            label={{
                                                                text: 'ssap.input.label.keep_dual_coverage',
                                                                labelTextReplacements: {
                                                                    personName: normalizeNameOfPerson(name),
                                                                    exchangeName
                                                                }
                                                            }}
                                                            inputData={{
                                                                isInputRequired: false,
                                                                type: INPUT_TYPES.CHECKBOX,
                                                                name: 'dualCoverage',
                                                                checked: dualCoverage || false,
                                                                value: dualCoverage || false,
                                                                currentData: householdMembers[member],
                                                                fields: ['healthCoverage', 'medicareInsurance'],
                                                                isInputStandalone: true
                                                            }}
                                                            inputActions={{
                                                                onFieldChange: onFieldChange_medicareInsurance
                                                            }}
                                                        />
                                                    </div>
                                                </fieldset>
                                            </div>
                                        }
                                    </Fragment>
                                )}
                            </Fragment>
                        )}

                        <div className='margin-t-30'>
                            <UniversalInput
                                legend={{
                                    legendText: 'ssap.input.legend.is_enrolled',
                                    legendTextReplacements: {
                                        personName: (
                                            <strong>
                                                {normalizeNameOfPerson(name)}
                                            </strong>
                                        ),
                                        note: (
                                            <strong>
                                                <FormattedMessage id='ssap.input.legend.is_enrolled.marketplace_note' />
                                            </strong>
                                        )
                                    },
                                    dataInstructions: 'instructional_text.coverage_currently_enrolled',
                                    replacements: { exchangeName, exchangeMedicaid }
                                }}
                                label={{
                                    showRequiredIcon: true
                                }}
                                inputData={{
                                    isInputRequired: true,
                                    type: INPUT_TYPES.RADIO_BUTTONS,
                                    name: 'currentlyHasHealthInsuranceIndicator',
                                    value: currentlyHasHealthInsuranceIndicator,
                                    options: DROPDOWN_OPTIONS.YES_NO,
                                    currentData: householdMembers[member],
                                    validationType: VALIDATION_TYPES.CHOSEN,
                                    fields: ['healthCoverage'],
                                    isInputStandalone: true

                                }}
                                inputActions={{
                                    onFieldChange
                                }}
                                errorClasses={['margin-l-0']}
                            />
                        </div>

                        {
                            currentlyHasHealthInsuranceIndicator &&

                            <Fragment>
                                <ValidationErrorMessage
                                    isVisible = { isFormSubmitted && !checkTopLevelValidation() }
                                    messageText = { VALIDATION_ERROR_MESSAGES.COVERAGE_TYPE }
                                    errorClasses = { [ 'margin-l-0' ] }
                                />
                                <div className = 'margin-t-30' >
                                    <fieldset className = 'usa-fieldset' >
                                        <legend
                                            data-instructions = 'coverage_type'
                                            className = 'gi-checkbox margin-b-10'
                                        >
                                            <span className = 'required-true' id = 'coverage_type_legend_text' >
                                                <FormattedMessage
                                                    id = 'ssap.additional_info.other_health_coverage.coverage_type'
                                                    values = { {
                                                        personName: (
                                                            <strong>
                                                                { normalizeNameOfPerson(name) }
                                                            </strong>
                                                        )
                                                    } }
                                                />
                                            </span>
                                            <InstructionalText
                                                Component = {
                                                    <span data-instructions = 'instructional_text.coverage_type' />
                                                }
                                                replacements = {{ exchangeName, exchangeMedicaid, exchangeChip, exchangeMedicaidOrChip }}
                                            />
                                        </legend>

                                        <ul className = 'usa-list usa-list--unstyled' role = 'group' aria-labelledby = 'coverage_type_legend_text'>
                                            {
                                                otherPrograms.map(program => {
                                                    return (
                                                        <Fragment key = { program.type } >
                                                            <li>
                                                                <UniversalInput
                                                                    label={{
                                                                        text: program.nameId,
                                                                        labelTextReplacements: { exchangeName }
                                                                    }}
                                                                    inputData={{
                                                                        isInputRequired: false,
                                                                        classes: ['my-0'],
                                                                        type: INPUT_TYPES.CHECKBOX,
                                                                        name: program.type,
                                                                        checked: program.eligible,
                                                                        value: program.eligible,
                                                                        currentData: cloneObject(program),
                                                                        fields: ['healthCoverage'],
                                                                        isInputStandalone: true,
                                                                        ...(program.type === OTHER_COVERAGE && {
                                                                            tooltip: {
                                                                                dataAutomationId: `other-coverage-tooltip-${member}`,
                                                                                index: `other-coverage-${member}`,
                                                                                content: 'ssap.tooltip.other_coverage',
                                                                                contentReplacement: { exchangeName },
                                                                            }
                                                                        })
                                                                    }}
                                                                    inputActions={{
                                                                        onFieldChange: onFieldChange_programType
                                                                    }}
                                                                />
                                                            </li>

                                                            {
                                                                program && program.type === OTHER_COVERAGE && program.eligible &&

                                                                <Fragment>
                                                                    <li>
                                                                        <UniversalInput
                                                                            label = { {
                                                                                text: 'ssap.input.label.insurance_name',
                                                                                labelClasses: [ 'spl__width--01' ]
                                                                            } }
                                                                            inputData = { {
                                                                                isInputRequired: false,
                                                                                type: INPUT_TYPES.REGULAR_INPUT,
                                                                                name: 'insuranceName',
                                                                                helperClasses: [],
                                                                                value: otherInsurance.insuranceName,
                                                                                placeholder: 'ssap.input.placeholder.insurance_name',
                                                                                validationType: VALIDATION_TYPES.NOT_EMPTY,
                                                                                currentData: otherInsurance,
                                                                                fields: [],
                                                                                isInputStandalone: true
                                                                            } }
                                                                            inputActions = { {
                                                                                onFieldChange: onFieldChange_otherInsurance
                                                                            } }
                                                                        />
                                                                    </li>
                                                                    <li>
                                                                        <UniversalInput
                                                                            label = { {
                                                                                text: 'ssap.input.label.policy_number',
                                                                                labelClasses: [ 'spl__width--01' ]
                                                                            } }
                                                                            inputData = { {
                                                                                isInputRequired: false,
                                                                                type: INPUT_TYPES.REGULAR_INPUT,
                                                                                name: 'policyNumber',
                                                                                helperClasses: [],
                                                                                value: program.name === 'Employer Insurance'
                                                                                    ? currentEmployerInsurance.policyNumber
                                                                                    : otherInsurance.policyNumber,
                                                                                placeholder: 'ssap.input.placeholder.policy_number',
                                                                                validationType: VALIDATION_TYPES.NOT_EMPTY,
                                                                                currentData: program.name === 'Employer Insurance'
                                                                                    ? currentEmployerInsurance
                                                                                    : otherInsurance,
                                                                                fields: [],
                                                                                isInputStandalone: true
                                                                            } }
                                                                            inputActions = { {
                                                                                onFieldChange: onFieldChange_otherInsurance
                                                                            } }
                                                                        />
                                                                    </li>
                                                                    {isState.MN && 
                                                                        <li>
                                                                            <UniversalInput
                                                                                label={{
                                                                                    labelClass: ['tablet:grid-col-4 gi-grid__layout-inline gi-col-layout__label'],
                                                                                    text: 'ssap.input.label.insurance_end_date',
                                                                                }}
                                                                                inputData={{
                                                                                    isInputRequired: false,
                                                                                    type: INPUT_TYPES.DATE_PICKER,
                                                                                    inputClasses: ['tablet:grid-col-6'],
                                                                                    name: 'endDate',
                                                                                    value: otherInsurance.endDate,
                                                                                    valuesToMatch: [serverDate, addToDate(TIME_INTERVALS.SIXTY_DAYS, 'days', serverDate)],
                                                                                    validationType: VALIDATION_TYPES.DATE_BETWEEN_TWO_DATES,
                                                                                    validationErrorMessage: 'ssap.validation_error_message.date_within',
                                                                                    validationErrorValues: { num: TIME_INTERVALS.SIXTY_DAYS, unit: 'days' },
                                                                                    currentData: otherInsurance,
                                                                                    fields: [],
                                                                                    isInputStandalone: true
                                                                                }}
                                                                                inputActions={{
                                                                                    onFieldChange: onFieldChange_otherInsurance
                                                                                }}
                                                                            />
                                                                        </li>
                                                                    }
                                                                    <li>
                                                                        <UniversalInput
                                                                            legend = { {
                                                                                legendText: 'ssap.input.legend.type_of_coverage'
                                                                            } }
                                                                            label = { {
                                                                                labelClass: [ 'tablet:grid-col-4 gi-radio_buttons gi-grid__layout-inline gi-col-layout__legend padding-r-1' ],
                                                                                ulClass: [ 'tablet:grid-col-6 gi-grid__layout-inline' ],
                                                                                showRequiredIcon: false
                                                                            } }
                                                                            inputData = { {
                                                                                isInputRequired: false,
                                                                                type: INPUT_TYPES.RADIO_BUTTONS,
                                                                                name: 'limitedBenefit',
                                                                                value: otherInsurance.limitedBenefit,
                                                                                options: DROPDOWN_OPTIONS.COVERAGE_TYPE,
                                                                                currentData: otherInsurance,
                                                                                fields: [],
                                                                                isInputStandalone: true
                                                                            } }
                                                                            inputActions = { {
                                                                                onFieldChange: onFieldChange_otherInsurance
                                                                            } }
                                                                        />
                                                                    </li>
                                                                </Fragment>
                                                            }
                                                        </Fragment>
                                                    );
                                                })
                                            }
                                        </ul>
                                    </fieldset>
                                </div>
                            </Fragment>
                        }
                    </div>
                </div>
            </Form>
        </Fragment>
    );
};

const mapStateToProps = state => ({

    data: state.ssap.data,
    environment: state.common.environment,
    form: state.ssap.form,

    householdMembers: state
        .ssap
        .data
        .taxHousehold
        .householdMember
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators({
        changeData
    }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(Page4_61);
